import { useState } from "react";
import { Product, OrderDetails } from "../types/restaurant";
import api from "src/features/auth/axiosConfig";
import { notifyError, notifySuccess, notifyWarning } from "src/assets/toastService";

interface UseOrderOperationsProps {
  selectedProducts: Product[];
  setSelectedProducts: (products: Product[]) => void;
  customerName: string;
  setCustomerName: (name: string) => void;
  phoneNumber: string;
  setPhoneNumber: (phone: string) => void;
  tableNumber: string;
  setTableNumber: (table: string) => void;
  discount: number;
  setDiscount: (discount: number) => void;
  oldProducts: OrderDetails | undefined;
  setOldProducts: (products: OrderDetails | undefined) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  guess: string;
  setGuess: (guess: string) => void;
  calculateSubtotal: () => number;
  calculateTotal: (subtotal: number) => number;
  isDelivery: boolean;
  headerRef: any;
  setCustomerAdd: (add: boolean) => void;
  setSelectedCategory: (category: string | null) => void;
  setSelectedCategoryradio: (category: string) => void;
  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  fetchCustomerDetails: () => Promise<void>;
  deliveryFee: number;
  setDeliveryFee: (fee: number) => void;
}

export const useOrderOperations = ({
  selectedProducts,
  setSelectedProducts,
  customerName,
  setCustomerName,
  phoneNumber,
  setPhoneNumber,
  tableNumber,
  setTableNumber,
  discount,
  setDiscount,
  oldProducts,
  setOldProducts,
  isEditing,
  setIsEditing,
  guess,
  setGuess,
  calculateSubtotal,
  calculateTotal,
  isDelivery,
  headerRef,
  setCustomerAdd,
  setSelectedCategory,
  setSelectedCategoryradio,
  setSearchQuery,
  setCurrentPage,
  fetchCustomerDetails,
  deliveryFee,
  setDeliveryFee,
}: UseOrderOperationsProps) => {
  const [open, setOpen] = useState(false);
  const [newOrderId, setNewOrderId] = useState<string>("");

  const resetFormFields = () => {
    setCustomerName("");
    setPhoneNumber("");
    setTableNumber("");
    setSelectedProducts([]);
    setDiscount(0);
    setDeliveryFee(0);
    setOldProducts(undefined);
    setIsEditing(false);
    setGuess("");
    setSelectedCategory(null);
    setSelectedCategoryradio("none");
    setSearchQuery("");
    setCurrentPage(0);
    window.history.replaceState({}, "", window.location.pathname);
  };

  const transformProductsForAPI = (products: Product[]) => {
    return products.map((product) => ({
      productId: product._id?.$oid,
      productName: product.ProductName,
      arabicword: product.ArabicName,
      notes: product.notes || "",
      quantity: product.quantity || 0,
      amount: product.Amount,
      totalPrice: (Number(product.Amount) * (product.quantity || 0)).toFixed(2),
      Type: product.Type,
    }));
  };

  const handleCancelOrder = async (adjust_type: string) => {
    const orderCancel = {
      OrderStatus: "Cancelled",
      // adjust_type: adjust_type
    };
    if (oldProducts) {
      api
        .put(`/app/v1/pos/order/${oldProducts.Orderid}`, orderCancel)
        .then(() => {
          notifySuccess("Order cancelled successfully!");
          resetFormFields();
        })
        .catch((error) => {
          console.error("Error cancelling the order:", error);
          notifyError("Failed to cancel the order. Please try again.");
        });
    } else {
      notifyWarning("No order to cancel!");
    }
  };

  const handleUpdateOrder = async (isOrderModified: boolean) => {
    if (isEditing && !isOrderModified) {
      notifyWarning("No changes detected to update.");
      return;
    }

    if (selectedProducts.length === 0) {
      notifyWarning("Please select at least one product!");
      return;
    }

    // Transform products to consistent structure for update
    const transformedProducts = transformProductsForAPI(selectedProducts);

    const orderTypeForUpdate = isDelivery ? "Delivery" : "Table";

    const addNewproducts: any = {
      guess: guess,
      table_number: tableNumber,
      OrderStatus: "pending",
      totalAmount: calculateSubtotal().toFixed(2),
      discount: discount,
      Finalamount: calculateTotal(calculateSubtotal()).toFixed(2),
      Products: transformedProducts, // Use transformed structure
      deliveryFee: isDelivery ? deliveryFee : 0, // deliveryFee only if delivery
      OrderType: orderTypeForUpdate, 
    };
    // Add delivery fee only for delivery orders
    if (isDelivery) {
      addNewproducts.deliveryFee = deliveryFee;
    }


    if (oldProducts) {
      try {
        const response = await api.put(`/app/v1/pos/order/${oldProducts.Orderid}`, addNewproducts);
        if (response.status === 200 || response.status === 201) {
          resetFormFields();
          if (headerRef.current) {
            await headerRef.current.refreshOrders();
          }
          notifySuccess("Order Updated successfully!");
          window.history.replaceState({}, "", window.location.pathname);
        } else {
          notifyError("Failed to update order.");
          throw new Error("Failed to update order");
        }
      } catch (error) {
        console.error("Error updating order:", error);
        notifyError("Failed to update order. Please try again.");
      }
    } else {
      setOpen(true);
    }
  };

  const sendorder = async (customerData: string) => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    const hours = String(currentDate.getHours()).padStart(2, "0");
    const minutes = String(currentDate.getMinutes()).padStart(2, "0");
    const seconds = String(currentDate.getSeconds()).padStart(2, "0");
    const newOrderId = `${month}${day}${hours}${minutes}${seconds}`;
    setNewOrderId(newOrderId);
    
    // Use the same transformation for consistency
    const transformedProducts = transformProductsForAPI(selectedProducts);
    
    const orderDetails = {
      Customerid: customerData,
      OrderStatus: "pending",
      guess: guess,
      table_number: tableNumber,
      totalAmount: calculateSubtotal().toFixed(2),
      discount: discount,
      deliveryFee: deliveryFee,
      Finalamount: calculateTotal(calculateSubtotal()).toFixed(2),
      Products: transformedProducts, // Use same transformed structure
      Orderdate: `${year}-${month}-${day}`,
      Ordertime: `${hours}:${minutes}:${seconds}`,
      OrderType: isDelivery ? "Delivery" : "Table",
      PaymentStatus: "Unpaid",
      outstanding_balance: 0,
      cancel_order_amount: 0,
      Tip: 0,
      RemainingAmount: calculateTotal(calculateSubtotal()).toFixed(2),
      payments: [{ Paymentmethod: "Cash", ReceivedAmount: 0 }],
      paymentHistory: [{ Paymentmethod: "Cash", ReceivedAmount: 0 }]
    };
    
    setOpen(false);

    try {
      await api.post(`/app/v1/pos/order`, orderDetails);
      resetFormFields();
      notifySuccess("Order created successfully!");
      return true;
    } catch (error) {
      console.error("Error sending order:", error);
      notifyError("Failed to create order. Please try again.");
      return false;
    }
  };

  const handleSubmit = async (customerId?: string) => {
    // If customerId is provided (existing customer), skip validation
    if (!customerId) {
      // Only validate for new customers
      if (!customerName || !phoneNumber) {
        notifyError("Customer Name and Phone Number are required.");
        return;
      }
    }

    if (customerId) {
      const success = await sendorder(customerId);
      if (success) {
        setCustomerAdd(false);
      }
      return;
    }

    const customerData = {
      Name: customerName,
      phone_number: phoneNumber,
      table_number: tableNumber,
    };

    try {
      const customerResponse = await api.post("/app/v1/pos/customer", customerData);
      await fetchCustomerDetails();
      const success = await sendorder(customerResponse.data._id);
      if (success) {
        setOpen(false);
        setCustomerAdd(false);
      }
    } catch (error: any) {
      console.error("Error creating customer:", error);
      if (error.response?.data?.message === "Phone Number already Exists") {
        notifyError("Phone Number already exists.");
      } else {
        notifyError("An unexpected error occurred.");
      }
    }
  };

  return {
    open,
    setOpen,
    newOrderId,
    handleCancelOrder,
    handleUpdateOrder,
    handleSubmit,
    sendorder,
    resetFormFields,
    transformProductsForAPI, // Export if needed elsewhere
  };
};